import { z } from 'zod'
import { contentVisibilitySchema } from './contentVisibility'
import { httpUrlSchema, idSchema, iconSchema, internalPathSchema } from './common'

export const widgetLayoutSchema = z
  .object({
    x: z.number().int().min(0).max(11),
    y: z.number().int().min(0).max(10000),
    w: z.number().int().min(1).max(12),
    h: z.number().int().min(1).max(20),
  })
  .strict()
  .refine((value) => value.x + value.w <= 12, 'Le widget dépasse la grille')
export const widgetActionSchema = z.union([
  z.object({ type: z.literal('none') }).strict(),
  z.object({ type: z.literal('modal') }).strict(),
  z
    .object({
      type: z.literal('link'),
      destination: z.literal('internal'),
      pageId: idSchema,
      target: z.enum(['_self', '_blank']).default('_self'),
    })
    .strict(),
  z
    .object({
      type: z.literal('link'),
      destination: z.literal('external'),
      url: httpUrlSchema.optional(),
      target: z.enum(['_self', '_blank']).default('_blank'),
    })
    .strict(),
])

const serviceIdsSchema = z
  .array(idSchema)
  .min(1)
  .max(100)
  .refine((ids) => new Set(ids).size === ids.length, 'Services dupliqués')
const monitoringUptimeServiceIdsSchema = z
  .array(idSchema)
  .min(1)
  .max(20)
  .refine((ids) => new Set(ids).size === ids.length, 'Services dupliqués')
export type MonitoringUptimeDisplay = 'cards' | 'list' | 'table'
export function monitoringUptimeMinimumSize(
  serviceCount: number,
  display: MonitoringUptimeDisplay,
  width: number,
) {
  const minW = display === 'table' ? 6 : 3
  const columns = display === 'cards' && width >= 8 ? 2 : 1
  const minH =
    display === 'cards'
      ? Math.max(2, Math.ceil(serviceCount / columns) * 2)
      : display === 'list'
        ? Math.max(2, Math.ceil((serviceCount * 55 + 59) / 92))
        : Math.max(2, Math.ceil((serviceCount * 41 + 90) / 92))
  return { minW, minH }
}
export type InventoryWidgetType =
  | 'inventory-summary'
  | 'inventory-my-assets'
  | 'inventory-low-stock'
  | 'inventory-expirations'
  | 'inventory-stock-tracker'
  | 'inventory-recent-movements'
export type TodoWidgetType =
  'todo-list' | 'todo-today' | 'todo-upcoming' | 'todo-overdue' | 'todo-counter' | 'todo-view'
export function inventoryWidgetMinimumSize(
  type: InventoryWidgetType,
  itemCount: number,
  width: number,
  display: 'cards' | 'bars' | 'compact' | 'tile' = 'compact',
) {
  if (type === 'inventory-summary') return { minW: 3, minH: 2 }
  if (type === 'inventory-stock-tracker') {
    if (display === 'tile') return { minW: 1, minH: 1 }
    const columns = display === 'cards' ? (width >= 8 ? 3 : width >= 5 ? 2 : 1) : 1
    const rowHeight = display === 'compact' ? 48 : display === 'bars' ? 64 : 82
    return {
      minW: display === 'cards' ? 4 : 3,
      minH: Math.min(
        12,
        Math.max(3, Math.ceil((Math.ceil(Math.max(1, itemCount) / columns) * rowHeight + 48) / 92)),
      ),
    }
  }
  const visibleItems = Math.min(
    Math.max(1, itemCount),
    type === 'inventory-recent-movements' ? 8 : 6,
  )
  return {
    minW: type === 'inventory-recent-movements' ? 4 : 3,
    minH: Math.min(
      10,
      Math.max(
        2,
        Math.ceil((visibleItems * (type === 'inventory-recent-movements' ? 58 : 48) + 18) / 92),
      ),
    ),
  }
}
const normalizeServiceSelection = <T extends { serviceId?: string; serviceIds?: string[] }>(
  value: T,
) => {
  const { serviceId, serviceIds, ...config } = value
  return { ...config, serviceIds: serviceIds ?? (serviceId ? [serviceId] : []) }
}
const serviceStatusConfigSchema = z
  .object({
    serviceId: idSchema.optional(),
    serviceIds: serviceIdsSchema.optional(),
    showLatency: z.boolean().default(true),
    showUptime: z.boolean().default(true),
  })
  .strict()
  .refine(
    (value) => Boolean(value.serviceId || value.serviceIds?.length),
    'Au moins un service est requis',
  )
  .transform(normalizeServiceSelection)
const monitoringUptimeConfigSchema = z
  .object({
    serviceId: idSchema.optional(),
    serviceIds: monitoringUptimeServiceIdsSchema.optional(),
    period: z.enum(['24h', '7d', '30d']).default('7d'),
    display: z.enum(['cards', 'list', 'table']).default('cards'),
  })
  .strict()
  .refine(
    (value) => Boolean(value.serviceId || value.serviceIds?.length),
    'Au moins un service est requis',
  )
  .transform(normalizeServiceSelection)
const monitoringLatencyConfigSchema = z
  .object({
    serviceId: idSchema.optional(),
    serviceIds: serviceIdsSchema.optional(),
    limit: z.number().int().min(5).max(100).default(30),
  })
  .strict()
  .refine(
    (value) => Boolean(value.serviceId || value.serviceIds?.length),
    'Au moins un service est requis',
  )
  .transform(normalizeServiceSelection)
const tlsStatusConfigSchema = z
  .object({ serviceId: idSchema.optional(), serviceIds: serviceIdsSchema.optional() })
  .strict()
  .refine(
    (value) => Boolean(value.serviceId || value.serviceIds?.length),
    'Au moins un service est requis',
  )
  .transform(normalizeServiceSelection)
const overallStatusConfigSchema = z
  .object({
    scope: z.enum(['all', 'selected']).default('all'),
    serviceIds: z
      .array(idSchema)
      .max(100)
      .refine((ids) => new Set(ids).size === ids.length, 'Services dupliqués')
      .default([]),
  })
  .strict()
  .refine((value) => value.scope === 'all' || value.serviceIds.length > 0, {
    path: ['serviceIds'],
    message: 'Au moins un service est requis',
  })
const inventoryConsumableIdsSchema = z
  .array(idSchema)
  .min(1)
  .max(20)
  .refine((ids) => new Set(ids).size === ids.length, 'Produits dupliqués')
const inventoryTrackedItemSchema = z
  .object({ kind: z.enum(['asset', 'consumable', 'license', 'kit']), id: idSchema })
  .strict()
const inventoryTrackedItemsSchema = z
  .array(inventoryTrackedItemSchema)
  .min(1)
  .max(20)
  .refine(
    (items) => new Set(items.map((item) => `${item.kind}:${item.id}`)).size === items.length,
    'Éléments dupliqués',
  )

export const widgetDefinitions = {
  'application-shortcut': {
    label: 'Raccourci application',
    description: 'Ouvre une application Morning',
    icon: 'package-search',
    size: { w: 3, h: 2, minW: 2, minH: 1, maxW: 6, maxH: 4 },
    clickable: true,
    supportsModal: true,
    config: z
      .object({
        applicationId: idSchema,
        showDescription: z.boolean().default(true),
        showBadge: z.boolean().default(true),
        showCategory: z.boolean().default(false),
        showMonitoringStatus: z.boolean().default(false),
      })
      .strict(),
  },
  'page-link': {
    label: 'Accès à une page',
    description: 'Navigation directe vers une page Morning',
    icon: 'file-text',
    size: { w: 3, h: 2, minW: 2, minH: 1, maxW: 6, maxH: 4 },
    clickable: true,
    supportsModal: true,
    config: z.object({ pageId: idSchema, showDescription: z.boolean().default(true) }).strict(),
  },
  notifications: {
    label: 'Notifications',
    description: 'Affiche les notifications du compte',
    icon: 'bell',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 8 },
    clickable: false,
    supportsModal: true,
    config: z
      .object({
        limit: z.number().int().min(1).max(20).default(5),
        scope: z.enum(['unread', 'all']).default('unread'),
        display: z.enum(['compact', 'detailed']).default('compact'),
        showCount: z.boolean().default(true),
      })
      .strict(),
  },
  'application-list': {
    label: 'Liste d’applications',
    description: 'Affiche des applications Morning',
    icon: 'layout-dashboard',
    size: { w: 6, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 12 },
    clickable: false,
    supportsModal: true,
    config: z
      .object({
        applicationIds: z
          .array(idSchema)
          .min(1)
          .max(100)
          .refine((ids) => new Set(ids).size === ids.length, 'Applications dupliquées'),
        display: z.enum(['list', 'icons']).default('list'),
        compact: z.boolean().default(false),
        showTitle: z.boolean().default(true),
        showDescription: z.boolean().default(false),
      })
      .strict(),
  },
  'service-status': {
    label: 'État des services',
    description: 'État, latence et disponibilité',
    icon: 'activity',
    size: { w: 4, h: 3, minW: 2, minH: 1, maxW: 12, maxH: 12 },
    clickable: true,
    supportsModal: true,
    config: serviceStatusConfigSchema,
  },
  'service-list-status': {
    label: 'Liste de services',
    description: 'État de plusieurs services',
    icon: 'server',
    size: { w: 6, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 12 },
    clickable: false,
    supportsModal: true,
    config: z
      .object({ serviceIds: serviceIdsSchema, compact: z.boolean().default(false) })
      .strict(),
  },
  'monitoring-uptime': {
    label: 'Disponibilité',
    description: 'Uptime et mini historique',
    icon: 'chart-no-axes-column-increasing',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 20 },
    clickable: false,
    supportsModal: true,
    config: monitoringUptimeConfigSchema,
  },
  'monitoring-latency': {
    label: 'Latence',
    description: 'Latence récente des services',
    icon: 'gauge',
    size: { w: 4, h: 3, minW: 2, minH: 1, maxW: 12, maxH: 12 },
    clickable: false,
    supportsModal: true,
    config: monitoringLatencyConfigSchema,
  },
  'monitoring-incidents': {
    label: 'Incidents monitoring',
    description: 'Événements et incidents récents',
    icon: 'triangle-alert',
    size: { w: 6, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 10 },
    clickable: false,
    supportsModal: true,
    config: z
      .object({ serviceIds: serviceIdsSchema, limit: z.number().int().min(1).max(50).default(10) })
      .strict(),
  },
  'tls-status': {
    label: 'Certificats TLS',
    description: 'Certificats et dates d’échéance',
    icon: 'shield-check',
    size: { w: 4, h: 3, minW: 2, minH: 1, maxW: 12, maxH: 12 },
    clickable: false,
    supportsModal: true,
    config: tlsStatusConfigSchema,
  },
  'monitoring-overall-status': {
    label: 'Santé globale',
    description: 'État moyen des services',
    icon: 'heart-pulse',
    size: { w: 1, h: 1, minW: 1, minH: 1, maxW: 4, maxH: 4 },
    clickable: false,
    supportsModal: true,
    config: overallStatusConfigSchema,
  },
  'inventory-summary': {
    label: 'Synthèse inventaire',
    description: 'Matériel, consommables, licences et alertes',
    icon: 'boxes',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 6 },
    clickable: true,
    supportsModal: true,
    config: z.object({ showAlerts: z.boolean().default(true) }).strict(),
  },
  'inventory-my-assets': {
    label: 'Mes ressources',
    description: 'Éléments qui me sont actuellement affectés',
    icon: 'laptop',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 10 },
    clickable: true,
    supportsModal: true,
    config: z.object({ limit: z.number().int().min(1).max(25).default(5) }).strict(),
  },
  'inventory-low-stock': {
    label: 'Stocks faibles',
    description: 'Consommables sous leur seuil',
    icon: 'package-minus',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 10 },
    clickable: true,
    supportsModal: true,
    config: z.object({ limit: z.number().int().min(1).max(25).default(5) }).strict(),
  },
  'inventory-expirations': {
    label: 'Échéances inventaire',
    description: 'Licences et garanties proches',
    icon: 'calendar-clock',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 10 },
    clickable: true,
    supportsModal: true,
    config: z
      .object({
        days: z.number().int().min(1).max(365).default(30),
        limit: z.number().int().min(1).max(25).default(5),
      })
      .strict(),
  },
  'inventory-stock-tracker': {
    label: 'Suivi de produits',
    description: 'Stocks, seuils et besoins de réapprovisionnement ciblés',
    icon: 'package-search',
    size: { w: 5, h: 4, minW: 1, minH: 1, maxW: 12, maxH: 12 },
    clickable: false,
    supportsModal: true,
    config: z
      .object({
        consumableIds: inventoryConsumableIdsSchema,
        display: z.enum(['cards', 'bars', 'compact', 'tile']).default('cards'),
        showTarget: z.boolean().default(true),
      })
      .strict(),
  },
  'inventory-recent-movements': {
    label: 'Derniers mouvements',
    description: 'Activité récente des éléments d’inventaire sélectionnés',
    icon: 'history',
    size: { w: 5, h: 4, minW: 3, minH: 2, maxW: 12, maxH: 12 },
    clickable: false,
    supportsModal: true,
    config: z
      .object({
        items: inventoryTrackedItemsSchema,
        limit: z.number().int().min(1).max(30).default(10),
      })
      .strict(),
  },
  'todo-list': {
    label: 'Mes tâches',
    description: 'Liste personnelle de tâches accessible à l’utilisateur',
    icon: 'list-todo',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 10 },
    clickable: true,
    supportsModal: false,
    config: z
      .object({
        filter: z.enum(['today', 'upcoming', 'overdue', 'completed', 'high-priority']).optional(),
        listId: idSchema.optional(),
        limit: z.number().int().min(1).max(25).default(5),
        display: z.enum(['compact', 'detailed']).default('compact'),
        showPriority: z.boolean().default(true),
        showDue: z.boolean().default(true),
      })
      .strict(),
  },
  'todo-view': {
    label: 'Vue To-Do',
    description: 'Version condensée d’une vue sauvegardée accessible',
    icon: 'panels-top-left',
    size: { w: 5, h: 4, minW: 3, minH: 2, maxW: 12, maxH: 10 },
    clickable: true,
    supportsModal: false,
    config: z
      .object({
        listId: idSchema,
        viewId: idSchema,
        mode: z.enum(['compact', 'comfortable']).default('compact'),
        limit: z.number().int().min(1).max(25).default(8),
      })
      .strict(),
  },
  'todo-today': {
    label: 'Aujourd’hui',
    description: 'Tâches à réaliser aujourd’hui',
    icon: 'calendar-days',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 10 },
    clickable: true,
    supportsModal: false,
    config: z
      .object({
        limit: z.number().int().min(1).max(25).default(5),
        display: z.enum(['compact', 'detailed']).default('compact'),
        showPriority: z.boolean().default(true),
        showDue: z.boolean().default(true),
      })
      .strict(),
  },
  'todo-upcoming': {
    label: 'À venir',
    description: 'Prochaines échéances',
    icon: 'calendar-range',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 10 },
    clickable: true,
    supportsModal: false,
    config: z
      .object({
        limit: z.number().int().min(1).max(25).default(5),
        display: z.enum(['compact', 'detailed']).default('compact'),
        showPriority: z.boolean().default(true),
        showDue: z.boolean().default(true),
      })
      .strict(),
  },
  'todo-overdue': {
    label: 'En retard',
    description: 'Tâches dont l’échéance est dépassée',
    icon: 'triangle-alert',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 10 },
    clickable: true,
    supportsModal: false,
    config: z
      .object({
        limit: z.number().int().min(1).max(25).default(5),
        display: z.enum(['compact', 'detailed']).default('compact'),
        showPriority: z.boolean().default(true),
        showDue: z.boolean().default(true),
      })
      .strict(),
  },
  'todo-counter': {
    label: 'Compteur To-Do',
    description: 'Compteur personnel selon un filtre rapide',
    icon: 'list-checks',
    size: { w: 2, h: 2, minW: 1, minH: 1, maxW: 4, maxH: 4 },
    clickable: true,
    supportsModal: false,
    config: z
      .object({
        filter: z
          .enum(['today', 'upcoming', 'overdue', 'completed', 'high-priority'])
          .default('today'),
      })
      .strict(),
  },
  'approvals-pending': {
    label: 'Approbations à traiter',
    description: 'Les demandes qui attendent votre décision',
    icon: 'workflow',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 8 },
    clickable: true,
    supportsModal: false,
    config: z.object({ limit: z.number().int().min(1).max(20).default(6) }).strict(),
  },
  'approvals-my-requests': {
    label: 'Mes demandes récentes',
    description: 'Le statut de vos demandes d’approbation',
    icon: 'workflow',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 8 },
    clickable: true,
    supportsModal: false,
    config: z.object({ limit: z.number().int().min(1).max(20).default(6) }).strict(),
  },
  'skills-my-competencies': {
    label: 'Mes compétences',
    description: 'Niveaux officiels et cibles personnelles',
    icon: 'brain-circuit',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 8 },
    clickable: true,
    supportsModal: false,
    config: z.object({ limit: z.number().int().min(1).max(20).default(6) }).strict(),
  },
  'skills-my-evaluations': {
    label: 'Mes évaluations à faire',
    description: 'Brouillons et évaluations demandées',
    icon: 'clipboard-check',
    size: { w: 4, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 8 },
    clickable: true,
    supportsModal: false,
    config: z.object({ limit: z.number().int().min(1).max(20).default(6) }).strict(),
  },
  'skills-team-progress': {
    label: 'Progression de mon équipe',
    description: 'Progression agrégée des collaborateurs reliés',
    icon: 'users',
    size: { w: 5, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 8 },
    clickable: true,
    supportsModal: false,
    config: z.object({ limit: z.number().int().min(1).max(20).default(6) }).strict(),
  },
  'skills-critical-gaps': {
    label: 'Écarts critiques',
    description: 'Cibles critiques non atteintes dans la portée autorisée',
    icon: 'triangle-alert',
    size: { w: 5, h: 3, minW: 3, minH: 2, maxW: 12, maxH: 8 },
    clickable: true,
    supportsModal: false,
    config: z.object({ limit: z.number().int().min(1).max(20).default(6) }).strict(),
  },
} as const

const resolvedApplicationSchema = z
  .object({
    id: idSchema,
    name: z.string(),
    description: z.string(),
    url: httpUrlSchema,
    icon: iconSchema.optional(),
    target: z.enum(['_self', '_blank']),
    category: z.string().optional(),
    badge: z
      .object({ label: z.string(), tone: z.enum(['neutral', 'blue', 'green', 'orange', 'red']) })
      .strict()
      .optional(),
  })
  .strict()
const resolvedPageSchema = z
  .object({
    id: idSchema,
    title: z.string(),
    description: z.string().optional(),
    path: internalPathSchema,
  })
  .strict()
const common = {
  id: idSchema,
  title: z.string().min(1).max(100).optional(),
  enabled: z.boolean().default(true),
  position: z.number().int().min(0).max(10000).default(0),
  layout: widgetLayoutSchema,
  action: widgetActionSchema.default({ type: 'none' }),
  visibility: contentVisibilitySchema.optional(),
  resolved: z
    .object({
      application: resolvedApplicationSchema.optional(),
      applications: z.array(resolvedApplicationSchema).optional(),
      page: resolvedPageSchema.optional(),
      actionUrl: z.string().optional(),
    })
    .strict()
    .optional(),
}
export const widgetSchema = z
  .discriminatedUnion('type', [
    z
      .object({
        ...common,
        type: z.literal('application-shortcut'),
        config: widgetDefinitions['application-shortcut'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('page-link'),
        config: widgetDefinitions['page-link'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('notifications'),
        config: widgetDefinitions.notifications.config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('application-list'),
        config: widgetDefinitions['application-list'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('service-status'),
        config: widgetDefinitions['service-status'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('service-list-status'),
        config: widgetDefinitions['service-list-status'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('monitoring-uptime'),
        config: widgetDefinitions['monitoring-uptime'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('monitoring-latency'),
        config: widgetDefinitions['monitoring-latency'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('monitoring-incidents'),
        config: widgetDefinitions['monitoring-incidents'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('tls-status'),
        config: widgetDefinitions['tls-status'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('monitoring-overall-status'),
        config: widgetDefinitions['monitoring-overall-status'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('inventory-summary'),
        config: widgetDefinitions['inventory-summary'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('inventory-my-assets'),
        config: widgetDefinitions['inventory-my-assets'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('inventory-low-stock'),
        config: widgetDefinitions['inventory-low-stock'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('inventory-expirations'),
        config: widgetDefinitions['inventory-expirations'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('inventory-stock-tracker'),
        config: widgetDefinitions['inventory-stock-tracker'].config,
      })
      .strict(),
    z
      .object({
        ...common,
        type: z.literal('inventory-recent-movements'),
        config: widgetDefinitions['inventory-recent-movements'].config,
      })
      .strict(),
    ...(
      [
        'todo-list',
        'todo-today',
        'todo-upcoming',
        'todo-overdue',
        'todo-counter',
        'todo-view',
      ] as const
    ).map((type) =>
      z
        .object({ ...common, type: z.literal(type), config: widgetDefinitions[type].config })
        .strict(),
    ),
    ...(
      [
        'approvals-pending',
        'approvals-my-requests',
        'skills-my-competencies',
        'skills-my-evaluations',
        'skills-team-progress',
        'skills-critical-gaps',
      ] as const
    ).map((type) =>
      z
        .object({ ...common, type: z.literal(type), config: widgetDefinitions[type].config })
        .strict(),
    ),
  ])
  .superRefine((widget, context) => {
    const size = widgetDefinitions[widget.type].size
    if (
      widget.layout.w < size.minW ||
      widget.layout.w > size.maxW ||
      widget.layout.h < size.minH ||
      widget.layout.h > size.maxH
    )
      context.addIssue({
        code: 'custom',
        path: ['layout'],
        message: 'Taille incompatible avec le type de widget',
      })
    if (widget.type === 'monitoring-overall-status' && widget.layout.w !== widget.layout.h)
      context.addIssue({
        code: 'custom',
        path: ['layout'],
        message: 'Le widget de santé globale doit rester carré',
      })
    if (
      widget.type === 'inventory-stock-tracker' &&
      widget.config.display === 'tile' &&
      (widget.config.consumableIds.length !== 1 ||
        widget.layout.w !== widget.layout.h ||
        widget.layout.w > 4)
    )
      context.addIssue({
        code: 'custom',
        path: ['config'],
        message: 'L’indicateur carré suit un seul produit et mesure entre 1 × 1 et 4 × 4',
      })
    if (
      widget.type === 'inventory-stock-tracker' &&
      widget.config.display !== 'tile' &&
      (widget.layout.w < 3 || widget.layout.h < 2)
    )
      context.addIssue({
        code: 'custom',
        path: ['layout'],
        message: 'Les vues multi-produits mesurent au minimum 3 × 2',
      })
    if (widget.action.type === 'modal' && !widgetDefinitions[widget.type].supportsModal)
      context.addIssue({ code: 'custom', path: ['action'], message: 'Modal indisponible' })
    if (
      widget.action.type === 'link' &&
      widget.action.destination === 'external' &&
      !widget.action.url &&
      widget.type !== 'application-shortcut'
    )
      context.addIssue({ code: 'custom', path: ['action', 'url'], message: 'URL externe requise' })
  })
export const dashboardSchema = z
  .object({
    placement: z.enum(['before-sections', 'after-sections']).default('before-sections'),
    widgets: z
      .array(widgetSchema)
      .max(100)
      .refine(
        (widgets) => new Set(widgets.map((widget) => widget.id)).size === widgets.length,
        'Identifiants de widgets dupliqués',
      ),
  })
  .strict()
  .superRefine((dashboard, context) => {
    const cells = new Set<string>()
    dashboard.widgets.forEach((widget, index) => {
      for (let y = widget.layout.y; y < widget.layout.y + widget.layout.h; y++)
        for (let x = widget.layout.x; x < widget.layout.x + widget.layout.w; x++) {
          const key = `${x}:${y}`
          if (cells.has(key))
            context.addIssue({
              code: 'custom',
              path: ['widgets', index, 'layout'],
              message: 'Chevauchement de widgets',
            })
          cells.add(key)
        }
    })
  })
export const configuredDashboardSchema = dashboardSchema.refine(
  (dashboard) => dashboard.widgets.every((widget) => !widget.resolved),
  'Les données résolues ne peuvent pas être définies dans le YAML',
)
export type DashboardWidget = z.infer<typeof widgetSchema>
export type Dashboard = z.infer<typeof dashboardSchema>

/** Declare requiredModule on a widget definition, never on client-supplied config. */
export function widgetRequiredModule(type: keyof typeof widgetDefinitions): string | undefined {
  return (widgetDefinitions[type] as { requiredModule?: string }).requiredModule
}
